import type { DayId } from '../domain/calendar.ts'
import type { DomainEvent, StoredEvent } from '../domain/events.ts'
import type { DeviceId, PhotoId } from '../domain/ids.ts'

/**
 * The seams of the application. Everything the domain cannot do for itself is a
 * port here, and every adapter under `adapters/` implements one of them. Two of
 * these — Clock and Random — exist so that time and chance are *inputs* that get
 * written into the event log, which is what makes replaying the log deterministic.
 */

export interface Clock {
  now(): Date
}

export interface Random {
  /** A draw in [0, 1). Recorded in the event, never recomputed. */
  float(): number
  /** A fresh opaque identifier. */
  id(): string
}

export interface ChainStatus {
  readonly ok: boolean
  readonly checked: number
  /** Sequence number where the chain first failed, if it did. */
  readonly brokenAt?: number
  readonly reason?: 'gap' | 'hash-mismatch'
  /** Which device's chain broke. A restored log holds one chain per device, and
   *  a sequence number on its own would not say whose. */
  readonly onDevice?: DeviceId
}

export interface EventStore {
  /** Appends in order, stamping id, device, sequence and hash. Never overwrites. */
  append(events: readonly DomainEvent[]): Promise<readonly StoredEvent[]>
  /** The whole log, every device, in replay order. This is what the projection
   *  replays and what a backup writes out — both have to see a restored log. */
  readAll(): Promise<readonly StoredEvent[]>
  /** Events after a sequence number, on this device's own chain. For sync; the
   *  replay goes through `readAll`. */
  readSince(seq: number): Promise<readonly StoredEvent[]>
  readDay(day: DayId): Promise<readonly StoredEvent[]>
  count(): Promise<number>
  head(): Promise<{ readonly seq: number; readonly hash: string } | null>
  /** Walks the hash chain. A gap or a mismatch means loss or tampering. */
  verify(): Promise<ChainStatus>
  /** Restores events from a backup. Ignores ids already present, so it is safe
   *  to import the same file twice. */
  importEvents(
    events: readonly StoredEvent[],
  ): Promise<{ readonly imported: number; readonly skipped: number }>
  deviceId(): DeviceId
}

export interface StoredPhoto {
  readonly id: PhotoId
  readonly bytes: number
  readonly createdAt: string
  readonly kind: 'reference' | 'result' | 'check'
}

export interface PhotoStore {
  put(blob: Blob, kind: StoredPhoto['kind']): Promise<PhotoId>
  /**
   * Devuelve una foto de una copia de seguridad, con el id que ya la nombra en
   * el registro. El registro guarda ids, no fotos: una foto restaurada con un id
   * nuevo no la reclama ninguna tarea y no se vería nunca.
   *
   * Salta la que ya está, para que importar dos veces el mismo archivo no
   * duplique nada ni pise lo que hay.
   */
  restore(photo: {
    readonly id: PhotoId
    readonly blob: Blob
    readonly kind: StoredPhoto['kind']
    readonly createdAt: string
  }): Promise<'imported' | 'skipped'>
  get(id: PhotoId): Promise<Blob | null>
  list(): Promise<readonly StoredPhoto[]>
  remove(id: PhotoId): Promise<void>
  /** Result photos older than the retention window. Reference photos never expire. */
  expired(now: Date, retentionDays: number): Promise<readonly PhotoId[]>
  totalBytes(): Promise<number>
}

export interface PinState {
  readonly isSet: boolean
  readonly consecutiveFailures: number
  readonly lastFailureAt: Date | null
}

/**
 * Which secret. The parent mode has one; each child may have their own, which is
 * what stops one of them walking into the other's screen and ticking things off
 * for a laugh.
 */
export type PinKey = string

export interface PinVault {
  state(key?: PinKey): Promise<PinState>
  /** Stores only a PBKDF2 hash and its salt. The PIN itself is never persisted. */
  set(pin: string, key?: PinKey): Promise<void>
  /** Verifies and updates the failure counter. Does not enforce the lockout;
   *  that is a domain rule the caller applies. */
  verify(pin: string, key?: PinKey): Promise<boolean>
  clearFailures(key?: PinKey): Promise<void>
  /** Takes a secret away entirely, so that door stops asking. */
  remove(key: PinKey): Promise<void>
}

/** The secret that guards one child's own screen. */
export function childPinKey(memberId: string): PinKey {
  return `child:${memberId}`
}

export interface SyncCursor {
  readonly serverSeq: number
}

/** Not implemented until phase 3. Declared now so nothing closes the door on it. */
export interface SyncGateway {
  push(events: readonly StoredEvent[]): Promise<void>
  pull(
    since: SyncCursor | null,
  ): Promise<{ readonly events: readonly StoredEvent[]; readonly cursor: SyncCursor }>
}

export interface Notifier {
  /** Best effort. A web app in the background cannot be relied on to fire. */
  notify(title: string, body: string): Promise<void>
  permission(): Promise<'granted' | 'denied' | 'default'>
  request(): Promise<boolean>
}
